import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { toApiError, useApi, type InvoiceBlockResponse } from '@/shared/api';

/**
 * Laden, Anlegen, Ändern, Entfernen und Umsortieren der wiederkehrenden Posten eines Apartments.
 *
 * <p>Die Reihenfolge liegt während des Ziehens lokal, damit die Zeile der Hand folgt und nicht
 * dem Netz; die Antwort des Servers ist danach die Wahrheit.</p>
 */
export function useInvoiceBlocks(propertyId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const queryKey = ['invoiceBlocks', propertyId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => api.invoiceBlocks.list(propertyId),
  });

  const [localOrder, setLocalOrder] = useState<InvoiceBlockResponse[] | null>(null);
  const blocks = useMemo<InvoiceBlockResponse[]>(
    () => localOrder ?? data ?? [],
    [localOrder, data],
  );

  const fail = async (err: unknown) => {
    const apiError = await toApiError(err);
    toast.error(apiError.detail ?? apiError.title);
  };

  const create = useMutation({
    mutationFn: (body: Parameters<typeof api.invoiceBlocks.create>[1]) =>
      api.invoiceBlocks.create(propertyId, body),
    onSuccess: () => {
      toast.success('Posten angelegt');
      void qc.invalidateQueries({ queryKey });
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof api.invoiceBlocks.update>[2];
    }) => api.invoiceBlocks.update(propertyId, id, body),
    onSuccess: () => {
      toast.success('Posten aktualisiert');
      void qc.invalidateQueries({ queryKey });
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.invoiceBlocks.delete(propertyId, id),
    onSuccess: () => {
      toast.success('Posten entfernt');
      void qc.invalidateQueries({ queryKey });
    },
    onError: fail,
  });

  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) => api.invoiceBlocks.reorder(propertyId, { orderedIds }),
    onSuccess: (server) => {
      setLocalOrder(null);
      qc.setQueryData(queryKey, server);
    },
    onError: async (err) => {
      setLocalOrder(null);
      await fail(err);
      void qc.invalidateQueries({ queryKey });
    },
  });

  return { blocks, isLoading, setLocalOrder, create, update, remove, reorder };
}
